/**
 * Use Idle Detection Hook
 *
 * Detects when the user has been idle (no mouse/keyboard activity) for a specified time.
 * Triggers a callback when idle threshold is reached.
 */

import { useEffect, useRef, useCallback } from 'react';

export const useIdleDetection = (
  onIdle: () => void,
  idleTime = 30000 // 30 seconds default
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasTriggeredRef = useRef(false);

  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      if (!hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        onIdle();
      }
    }, idleTime);
  }, [onIdle, idleTime]);

  useEffect(() => {
    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add listeners
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    // Initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer]);

  // Reset triggered flag when component remounts
  useEffect(() => {
    hasTriggeredRef.current = false;
  }, []);
};
